import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Button from '@/components/Buttons/Button';
import { ACCENT_COLORS, ACCENT_TEXT_COLORS } from '@/constants/Colors';
import { useUIStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import { accountApi } from '@/lib/api';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuCheckboxItem from '@/components/DropdownMenu/DropdownMenuCheckboxItem';
import SwitchInput from '@/components/Inputs/Switch';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, CHAT_INPUT_STYLE } from '@/constants/config';
import Tabs from '@/components/Tabs';
import { getPetById } from '@/constants/pets';
import MacFontSizeSettings from './MacFontSizeSettings';
import { AppearanceGroup, AppearanceRow, AppearanceThemePicker, AppearanceColorPicker, AppearanceChatPreview } from './AppearanceControls';

// Native menus inspect their direct children without rendering custom item
// components. Return primitive elements so Zeego can read titles and actions.
const nativeMenuChoices = (options, selectedValue, onChange, getLabel) => Object.values(options).map(value => (
  <DropdownMenu.CheckboxItem
    key={value}
    value={selectedValue === value ? 'on' : 'off'}
    onValueChange={() => onChange(value)}
  >
    <DropdownMenu.ItemTitle>{getLabel(value)}</DropdownMenu.ItemTitle>
    <DropdownMenu.ItemIndicator />
  </DropdownMenu.CheckboxItem>
));

function AppearanceSettings() {
  const { t } = useTranslation();
  const usesNativeMenus = Platform.OS === 'ios' || Platform.OS === 'android';
  const { isXS } = useResponsiveness();
  const { colors, theme, setTheme, accentBackgroundColor, setAccentBackgroundColor, accentTextColor, setAccentTextColor } = useTheme();
  // Older/default preferences store white as a hex value instead of its option ID.
  const selectedAccentTextColor = ACCENT_TEXT_COLORS.find(option =>
    option.id === accentTextColor || option.color.toLowerCase() === accentTextColor?.toLowerCase()
  )?.id || accentTextColor;
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const setMessageLayout = useUIStore(state => state.setMessageLayout);
  const setMessageWidth = useUIStore(state => state.setMessageWidth);
  const increaseBorderContrast = useUIStore(state => state.increaseBorderContrast);
  const setIncreaseBorderContrast = useUIStore(state => state.setIncreaseBorderContrast);
  const showTimestamps = useUIStore(state => state.showTimestamps);
  const setShowTimestamps = useUIStore(state => state.setShowTimestamps);
  const showMessageActions = useUIStore(state => state.showMessageActions);
  const setShowMessageActions = useUIStore(state => state.setShowMessageActions);
  const showSourcesOutputsPanel = useUIStore(state => state.showSourcesOutputsPanel !== false);
  const setShowSourcesOutputsPanel = useUIStore(state => state.setShowSourcesOutputsPanel);
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const setChatInputStyle = useUIStore(state => state.setChatInputStyle);
  const enterToSend = useUIStore(state => state.enterToSend);
  const setEnterToSend = useUIStore(state => state.setEnterToSend);
  const keepScreenAwake = useUIStore(state => state.keepScreenAwakeInChats === true);
  const setKeepScreenAwake = useUIStore(state => state.setKeepScreenAwakeInChats);
  const [activeTab, setActiveTab] = useState('style');

  // Follow-up suggestions toggle is a user-level (DB-backed) preference so
  // it follows the account across devices and the backend can skip the
  // extra inference call entirely when the user has opted out.
  const userChatPreferences = useAuthStore(state => state.user?.preferences?.chat);
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const selectedPetId = useAuthStore(state => state.user?.preferences?.appearance?.selectedPet || null);
  const showDesktopPet = useUIStore(state => state.showDesktopPet === true);
  const setShowDesktopPet = useUIStore(state => state.setShowDesktopPet);
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const [showFollowUpSuggestions, setShowFollowUpSuggestions] = useState(
    userChatPreferences?.followUpSuggestionsEnabled !== false
  );
  const [isUpdatingFollowUps, setIsUpdatingFollowUps] = useState(false);
  const [showCollectionSuggestions, setShowCollectionSuggestions] = useState(
    userChatPreferences?.collectionSuggestionsEnabled !== false
  );
  const [isUpdatingCollectionSuggestions, setIsUpdatingCollectionSuggestions] = useState(false);
  const [showStockCharts, setShowStockCharts] = useState(
    userChatPreferences?.stockChartsEnabled !== false
  );
  const [isUpdatingStockCharts, setIsUpdatingStockCharts] = useState(false);
  const [showWorkModeSuggestions, setShowWorkModeSuggestions] = useState(
    userChatPreferences?.workModeSuggestionsEnabled !== false
  );
  const [isUpdatingWorkModeSuggestions, setIsUpdatingWorkModeSuggestions] = useState(false);

  useEffect(() => {
    setShowFollowUpSuggestions(userChatPreferences?.followUpSuggestionsEnabled !== false);
  }, [userChatPreferences?.followUpSuggestionsEnabled]);

  useEffect(() => {
    setShowCollectionSuggestions(userChatPreferences?.collectionSuggestionsEnabled !== false);
  }, [userChatPreferences?.collectionSuggestionsEnabled]);

  useEffect(() => {
    setShowStockCharts(userChatPreferences?.stockChartsEnabled !== false);
  }, [userChatPreferences?.stockChartsEnabled]);

  useEffect(() => {
    setShowWorkModeSuggestions(userChatPreferences?.workModeSuggestionsEnabled !== false);
  }, [userChatPreferences?.workModeSuggestionsEnabled]);

  const handleFollowUpSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingFollowUps) return;
    const previous = showFollowUpSuggestions;
    setShowFollowUpSuggestions(value);
    setIsUpdatingFollowUps(true);
    try {
      await accountApi.updateChatPreferences({ followUpSuggestionsEnabled: value });
      updateUserPreferences({ chat: { followUpSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showFollowUpSuggestions.enabled')
          : t('settings.appearance.showFollowUpSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating follow-up suggestions preference:', error);
      setShowFollowUpSuggestions(previous);
      addToast(t('settings.appearance.showFollowUpSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingFollowUps(false);
    }
  }, [isUpdatingFollowUps, showFollowUpSuggestions, updateUserPreferences, addToast, t]);

  const handleCollectionSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingCollectionSuggestions) return;
    const previous = showCollectionSuggestions;
    setShowCollectionSuggestions(value);
    setIsUpdatingCollectionSuggestions(true);
    try {
      await accountApi.updateChatPreferences({ collectionSuggestionsEnabled: value });
      updateUserPreferences({ chat: { collectionSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showCollectionSuggestions.enabled')
          : t('settings.appearance.showCollectionSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating collection suggestions preference:', error);
      setShowCollectionSuggestions(previous);
      addToast(t('settings.appearance.showCollectionSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingCollectionSuggestions(false);
    }
  }, [isUpdatingCollectionSuggestions, showCollectionSuggestions, updateUserPreferences, addToast, t]);

  const handleEnterToSendToggle = useCallback(async (value) => {
    const next = value === true;
    setEnterToSend(next);
    if (!useAuthStore.getState().user?.id) return;
    try {
      await accountApi.updateChatPreferences({ enterToSend: next });
      updateUserPreferences({ chat: { enterToSend: next } });
    } catch (error) {
      console.error('Error updating enter-to-send preference:', error);
    }
  }, [setEnterToSend, updateUserPreferences]);

  const handleWorkModeSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingWorkModeSuggestions) return;
    const previous = showWorkModeSuggestions;
    setShowWorkModeSuggestions(value);
    setIsUpdatingWorkModeSuggestions(true);
    try {
      await accountApi.updateChatPreferences({ workModeSuggestionsEnabled: value });
      updateUserPreferences({ chat: { workModeSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showWorkModeSuggestions.enabled')
          : t('settings.appearance.showWorkModeSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating work mode suggestions preference:', error);
      setShowWorkModeSuggestions(previous);
      addToast(t('settings.appearance.showWorkModeSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingWorkModeSuggestions(false);
    }
  }, [isUpdatingWorkModeSuggestions, showWorkModeSuggestions, updateUserPreferences, addToast, t]);

  const handleStockChartsToggle = useCallback(async (value) => {
    if (isUpdatingStockCharts) return;
    const previous = showStockCharts;
    setShowStockCharts(value);
    setIsUpdatingStockCharts(true);
    try {
      await accountApi.updateChatPreferences({ stockChartsEnabled: value });
      updateUserPreferences({ chat: { stockChartsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showStockCharts.enabled')
          : t('settings.appearance.showStockCharts.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating stock charts preference:', error);
      setShowStockCharts(previous);
      addToast(t('settings.appearance.showStockCharts.updateFailed'), 'error');
    } finally {
      setIsUpdatingStockCharts(false);
    }
  }, [isUpdatingStockCharts, showStockCharts, updateUserPreferences, addToast, t]);

  const selectedPet = useMemo(() => getPetById(selectedPetId), [selectedPetId]);
  const conversationOptions = [
    { id: 'showTimestamps', value: showTimestamps, onChange: setShowTimestamps },
    { id: 'showMessageActions', value: showMessageActions, onChange: setShowMessageActions },
    ...(Platform.OS === 'web' && !isXS ? [{ id: 'showSourcesOutputsPanel', value: showSourcesOutputsPanel, onChange: setShowSourcesOutputsPanel }] : []),
  ];
  const suggestionOptions = [
    { id: 'showFollowUpSuggestions', value: showFollowUpSuggestions, onChange: handleFollowUpSuggestionsToggle, disabled: isUpdatingFollowUps },
    { id: 'showCollectionSuggestions', value: showCollectionSuggestions, onChange: handleCollectionSuggestionsToggle, disabled: isUpdatingCollectionSuggestions },
    { id: 'showWorkModeSuggestions', value: showWorkModeSuggestions, onChange: handleWorkModeSuggestionsToggle, disabled: isUpdatingWorkModeSuggestions },
    { id: 'showStockCharts', value: showStockCharts, onChange: handleStockChartsToggle, disabled: isUpdatingStockCharts },
  ];
  const comfortOptions = [
    { id: 'increaseBorderContrast', value: increaseBorderContrast, onChange: setIncreaseBorderContrast },
    ...(['web', 'macos'].includes(Platform.OS) ? [{ id: 'enterToSend', value: enterToSend !== false, onChange: handleEnterToSendToggle }] : []),
    ...(['ios', 'android'].includes(Platform.OS) ? [{ id: 'keepScreenAwake', value: keepScreenAwake, onChange: setKeepScreenAwake }] : []),
  ];
  const renderToggle = (option, first = false) => <AppearanceRow key={option.id} first={first}
    title={t(`settings.appearance.${option.id}.title`)} subtitle={t(`settings.appearance.${option.id}.subtitle`)}>
    <SwitchInput accessibilityLabel={t(`settings.appearance.${option.id}.title`)}
      value={option.value} onValueChange={option.onChange} disabled={option.disabled} />
  </AppearanceRow>;

  return (
    <View testID="appearance-settings">
      <AppearanceGroup title={t('settings.appearance.theme')} subtitle={t('settings.appearance.design.themeHelp')} card={false}>
        <AppearanceThemePicker selected={theme} onChange={setTheme} />
      </AppearanceGroup>

      <AppearanceGroup title={t('settings.appearance.design.colors')} subtitle={t('settings.appearance.design.colorsHelp')}>
        <View style={styles.colorCard}>
          <AppearanceColorPicker title={t('settings.appearance.accentBackgroundColor')}
            options={ACCENT_COLORS} selected={accentBackgroundColor} onChange={setAccentBackgroundColor} />
          <View style={{ borderTopWidth: StyleSheet.hairlineWidth, borderColor: colors.border_color_secondary }}>
            <AppearanceColorPicker title={t('settings.appearance.accentTextColor')}
              options={ACCENT_TEXT_COLORS} selected={selectedAccentTextColor} onChange={setAccentTextColor} />
          </View>
        </View>
      </AppearanceGroup>

      <AppearanceGroup title={t('settings.appearance.design.conversation')} subtitle={t('settings.appearance.design.conversationHelp')}>
        <AppearanceChatPreview messageLayout={messageLayout} messageWidth={messageWidth} chatInputStyle={chatInputStyle}
          showTimestamps={showTimestamps} showMessageActions={showMessageActions} />
      <AppearanceRow first stacked={isXS} title={t('settings.appearance.messageLayout.title')} subtitle={t('settings.appearance.design.layoutHelp')}>
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild={!usesNativeMenus}>
              <Button
                accessibilityLabel={t('settings.appearance.messageLayout.title')}
                text={`${t(`settings.appearance.messageLayout.${messageLayout.toLowerCase()}`)} • ${t(`settings.appearance.messageWidth.${messageWidth.toLowerCase()}`)}`}
                variant="secondary"
                size={isXS ? 'large' : 'small'}
                appendIcon="chevron-down"
                appendIconSize={12}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent" style={{ minWidth: 200 }}>
              {usesNativeMenus ? (
                <>
                  <DropdownMenu.Sub key="style">
                    <DropdownMenu.SubTrigger key="style-trigger">
                      <DropdownMenu.ItemTitle>{t('settings.appearance.style')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent>
                      {nativeMenuChoices(MESSAGE_LAYOUT, messageLayout, setMessageLayout,
                        value => t(`settings.appearance.messageLayout.${value}`))}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Sub>
                  <DropdownMenu.Sub key="width">
                    <DropdownMenu.SubTrigger key="width-trigger">
                      <DropdownMenu.ItemTitle>{t('settings.appearance.width')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent>
                      {nativeMenuChoices(MESSAGE_WIDTH, messageWidth, setMessageWidth,
                        value => t(`settings.appearance.messageWidth.${value}`))}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Sub>
                </>
              ) : (
                <>
                  <View style={styles.tabsContainer}>
                    <Tabs
                      size='s'
                      tabs={[
                        {
                          id: 'style',
                          label: t('settings.appearance.style'),
                          active: activeTab === 'style',
                          onPress: () => setActiveTab('style')
                        },
                        {
                          id: 'width',
                          label: t('settings.appearance.width'),
                          active: activeTab === 'width',
                          onPress: () => setActiveTab('width')
                        }
                      ]}
                    />
                  </View>

                  {activeTab === 'style' && Object.values(MESSAGE_LAYOUT).map(layout => (
                    <DropdownMenuCheckboxItem
                      key={layout}
                      onValueChange={() => setMessageLayout(layout.toLowerCase())}
                      isActive={messageLayout?.toLowerCase() === layout.toLowerCase()}
                      label={t(`settings.appearance.messageLayout.${layout.toLowerCase()}`)}
                    />
                  ))}

                  {activeTab === 'width' && Object.values(MESSAGE_WIDTH).map(width => (
                    <DropdownMenuCheckboxItem
                      key={width}
                      onValueChange={() => setMessageWidth(width.toLowerCase())}
                      isActive={messageWidth?.toLowerCase() === width.toLowerCase()}
                      label={t(`settings.appearance.messageWidth.${width.toLowerCase()}`)}
                    />
                  ))}
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </AppearanceRow>

      <AppearanceRow stacked={isXS} title={t('settings.appearance.chatInputStyle.title')} subtitle={t('settings.appearance.design.inputHelp')}>
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild={!usesNativeMenus}>
              <Button
                accessibilityLabel={t('settings.appearance.chatInputStyle.title')}
                text={t(`settings.appearance.chatInputStyle.${chatInputStyle.toLowerCase()}`)}
                variant="secondary"
                size={isXS ? 'large' : 'small'}
                appendIcon="chevron-down"
                appendIconSize={12}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              {usesNativeMenus ? nativeMenuChoices(CHAT_INPUT_STYLE, chatInputStyle, setChatInputStyle,
                value => t(`settings.appearance.chatInputStyle.${value}`)) : Object.values(CHAT_INPUT_STYLE).map(style => (
                <DropdownMenuCheckboxItem
                  key={style}
                  onValueChange={() => setChatInputStyle(style.toLowerCase())}
                  isActive={chatInputStyle?.toLowerCase() === style.toLowerCase()}
                  label={t(`settings.appearance.chatInputStyle.${style.toLowerCase()}`)}
                />
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </AppearanceRow>


        {conversationOptions.map(option => renderToggle(option))}
      </AppearanceGroup>

      <AppearanceGroup title={t('settings.appearance.design.extras')} subtitle={t('settings.appearance.design.extrasHelp')}>
        {suggestionOptions.map((option, index) => renderToggle(option, index === 0))}
        {(Platform.OS === 'web' || Platform.OS === 'macos') && <AppearanceRow stacked={isXS} title={t('pets.settingsTitle')} subtitle={t('pets.settingsSubtitle')}>
          <Button text={selectedPet ? selectedPet.name : t('pets.none')} accessibilityLabel={t('pets.settingsTitle')}
            variant="secondary" size={isXS ? 'large' : 'small'} appendIcon="chevron-right" appendIconSize={12} onPress={() => openModal('PetPickerModal')} />
        </AppearanceRow>}
        {(Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS)) && renderToggle({
          id: 'showDesktopPet',
          value: showDesktopPet,
          onChange: value => {
            setShowDesktopPet(value);
            if (value && !selectedPet) openModal('PetPickerModal');
          },
        })}
      </AppearanceGroup>

      <AppearanceGroup title={t('settings.appearance.design.comfort')} subtitle={t('settings.appearance.design.comfortHelp')}>
        {comfortOptions.map((option, index) => renderToggle(option, index === 0))}
        {Platform.OS === 'macos' && <View style={styles.fontSize}><MacFontSizeSettings /></View>}
      </AppearanceGroup>
    </View>
  );
}

const styles = StyleSheet.create({
  colorCard: { paddingHorizontal: 18 },
  menuBarSetting: { flexDirection: 'row', alignItems: 'center' },
  tabsContainer: { flexDirection: 'column', width: '100%', marginBottom: 8 },
  fontSize: { paddingHorizontal: 18 },
});

export default React.memo(AppearanceSettings);
